// Dashboard "bini" — hero saludo + KPIs reales + calendario protagónico del mes
// + últimos trades. Si no hay cuenta, onboarding mínimo.

import { useState, type ReactElement } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { Trade, TradingAccount } from '@bini-trading/shared-types';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';
import { AppLayout } from '../components/AppLayout';
import { Button } from '../components/ui/Button';
import { Card, Stat, Section } from '../components/ui/Card';
import { MonthCalendar, type DayAggregate } from '../components/calendar/MonthCalendar';
import { TradeDetailModal } from '../components/trades/TradeDetailModal';
import { TradeFormModal } from '../components/trades/TradeFormModal';

interface CalendarResponse {
  month: string;
  days: DayAggregate[];
}
interface SummaryResponse {
  tradesTotal: number;
  tradesClosed: number;
  tradesOpen: number;
  wins: number;
  losses: number;
  breakeven: number;
  winrate: number;
  totalPnl: number;
  totalFees: number;
  avgWin: number;
  avgLoss: number;
  rr: number;
  maxDrawdown: number;
  bySymbol: { symbol: string; trades: number; pnl: number; wins: number }[];
}
interface PayoutSummary {
  totalCobrado: number;
  totalPendiente: number;
  cantidadPagos: number;
  cantidadPendientes: number;
  promedioPago: number;
  ultimoPago: string | null;
  proximaVentanaEstimada: string | null;
  cicloDias: number;
  currency: string;
}
interface The5ersResponse {
  cuenta: string;
  broker: string;
  kind: string;
  initialBalance: number;
  balance: number;
  totalPnl: number;
  profitPct: number;
  targetPct: number;
  faltaTargetPct: number;
  targetAlcanzado: boolean;
  pnlHoy: number;
  ddDiaPct: number;
  maxDiaPct: number;
  ddTotalPct: number;
  maxTotalPct: number;
  diasRentables: number;
  minDays: number;
  tradesCerrados: number;
}

const fmtMoney = (n: number, opts: Intl.NumberFormatOptions = {}): string =>
  new Intl.NumberFormat('es-CL', { maximumFractionDigits: 0, ...opts }).format(n);
const fmtPct = (n: number): string => `${(n * 100).toFixed(1)}%`;

function currentMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

const MONTH_LABELS = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];

function greetingByHour(): string {
  const h = new Date().getHours();
  if (h < 6) return 'Madrugando';
  if (h < 13) return 'Buenos días';
  if (h < 19) return 'Buenas tardes';
  return 'Buenas noches';
}

export default function Dashboard(): ReactElement {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [detailTrade, setDetailTrade] = useState<Trade | null>(null);
  const month = currentMonth();

  const { data: accounts } = useQuery({
    queryKey: ['accounts'],
    queryFn: () => api<TradingAccount[]>('/accounts'),
  });
  const accountId = accounts && accounts.length > 0 ? accounts[0]!.id : '';

  const { data: summary } = useQuery({
    queryKey: ['analytics-summary', accountId],
    queryFn: () =>
      api<SummaryResponse>(`/analytics/summary${accountId ? `?accountId=${accountId}` : ''}`),
    enabled: !!accountId,
  });

  const { data: the5ers } = useQuery({
    queryKey: ['the5ers', accountId],
    queryFn: () =>
      api<The5ersResponse>(`/analytics/the5ers${accountId ? `?accountId=${accountId}` : ''}`),
    enabled: !!accountId,
  });

  const { data: payoutSummary } = useQuery({
    queryKey: ['payouts-summary', accountId],
    queryFn: () =>
      api<PayoutSummary>(`/payouts/summary${accountId ? `?accountId=${accountId}` : ''}`),
    enabled: !!accountId,
  });

  const { data: calendar } = useQuery({
    queryKey: ['analytics-calendar', accountId, month],
    queryFn: () =>
      api<CalendarResponse>(
        `/analytics/calendar?month=${month}${accountId ? `&accountId=${accountId}` : ''}`,
      ),
    enabled: !!accountId,
  });

  const { data: recentTrades } = useQuery({
    queryKey: ['trades-recent', accountId],
    queryFn: async () => {
      const all = await api<Trade[]>(`/trades${accountId ? `?accountId=${accountId}&limit=8` : '?limit=8'}`);
      return all.slice(0, 6);
    },
    enabled: !!accountId,
  });

  const noAccounts = !accounts || accounts.length === 0;
  const [, monthIdx] = month.split('-');
  const monthLabel = `${MONTH_LABELS[Number(monthIdx) - 1]} ${month.slice(0, 4)}`;

  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-8 sm:py-10">
        {/* Hero */}
        <header className="mb-8 sm:mb-10 flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-[11px] text-ink-subtle tracking-[0.22em] uppercase mb-2">
              {greetingByHour()}
            </div>
            <h1 className="font-display text-4xl sm:text-5xl tracking-tight text-ink leading-tight">
              {user?.name?.split(' ')[0] ?? 'Trader'}
              <span className="text-accent-500">.</span>
            </h1>
            <p className="text-ink-muted mt-2 text-sm">
              {noAccounts
                ? 'Configurá tu primera cuenta para empezar a registrar trades.'
                : `Tu mes de ${monthLabel} — un vistazo del journal.`}
            </p>
          </div>
          {!noAccounts && (
            <div className="flex items-center gap-2">
              <Button variant="secondary" size="md" onClick={() => navigate('/calendar')}>
                Ver calendario
              </Button>
              <Button variant="primary" size="md" onClick={() => setCreating(true)}>
                + Nuevo trade
              </Button>
            </div>
          )}
        </header>

        {noAccounts ? (
          <OnboardingHero />
        ) : (
          <>
            {/* Cockpit The5ers (cuentas de fondeo) */}
            {the5ers && (the5ers.kind === 'evaluation' || the5ers.kind === 'funded') && (
              <The5ersCockpit d={the5ers} />
            )}

            {/* Widget de payouts — solo si la cuenta es fondeada o ya hay actividad */}
            {payoutSummary &&
              (the5ers?.kind === 'funded' ||
                payoutSummary.cantidadPagos + payoutSummary.cantidadPendientes > 0) && (
                <PayoutWidget s={payoutSummary} />
              )}

            {/* KPIs */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
              <Stat
                label="P&L total"
                value={summary ? fmtMoney(summary.totalPnl, { signDisplay: 'always' }) : '—'}
                hint={summary ? `${summary.tradesClosed} trades cerrados` : ' '}
                tone={summary ? (summary.totalPnl > 0 ? 'win' : summary.totalPnl < 0 ? 'loss' : 'neutral') : 'neutral'}
                trend={summary ? (summary.totalPnl > 0 ? 'up' : summary.totalPnl < 0 ? 'down' : null) : null}
              />
              <Stat
                label="Winrate"
                value={summary && summary.tradesClosed > 0 ? fmtPct(summary.winrate) : '—'}
                hint={summary ? `${summary.wins}W · ${summary.losses}L · ${summary.breakeven}BE` : ' '}
                tone="accent"
              />
              <Stat
                label="Risk / Reward"
                value={summary && summary.rr > 0 ? `1 : ${summary.rr.toFixed(2)}` : '—'}
                hint={
                  summary
                    ? `avg win ${fmtMoney(summary.avgWin)} / loss ${fmtMoney(summary.avgLoss)}`
                    : ' '
                }
              />
              <Stat
                label="Drawdown máx"
                value={summary ? fmtMoney(summary.maxDrawdown) : '—'}
                hint="peak-to-trough"
                tone={summary && summary.maxDrawdown > 0 ? 'loss' : 'neutral'}
              />
            </div>

            {/* Main grid: calendar + sidebar */}
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Calendar protagónico */}
              <div className="lg:col-span-2">
                <Section
                  title={monthLabel}
                  action={
                    <Link
                      to="/calendar"
                      className="text-[11px] uppercase tracking-[0.18em] text-ink-subtle hover:text-accent-500 transition-colors"
                    >
                      Ver detalle →
                    </Link>
                  }
                >
                  <MonthCalendar
                    month={month}
                    days={calendar?.days ?? []}
                    selectedDate={null}
                    onSelectDate={(d) => d && navigate(`/calendar?date=${d}`)}
                  />
                  <CalendarLegend />
                </Section>
              </div>

              {/* Aside: top symbols + recent trades */}
              <div className="lg:col-span-1 space-y-6">
                <Section title="Top símbolos">
                  <Card padded={false} className="p-4">
                    {summary && summary.bySymbol.length > 0 ? (
                      <div className="divide-y divide-surface-border">
                        {summary.bySymbol.slice(0, 5).map((s) => {
                          const wr = s.trades > 0 ? s.wins / s.trades : 0;
                          return (
                            <div
                              key={s.symbol}
                              className="flex items-center justify-between py-2.5 first:pt-0 last:pb-0"
                            >
                              <div className="min-w-0">
                                <div className="font-mono text-sm font-bold text-ink truncate">
                                  {s.symbol}
                                </div>
                                <div className="text-[10px] text-ink-subtle">
                                  {s.trades} trades · {s.trades > 0 ? fmtPct(wr) : '—'} WR
                                </div>
                              </div>
                              <div
                                className={`font-mono text-sm font-bold tabular-nums ${
                                  s.pnl > 0
                                    ? 'text-win-DEFAULT'
                                    : s.pnl < 0
                                    ? 'text-loss-DEFAULT'
                                    : 'text-ink-muted'
                                }`}
                              >
                                {fmtMoney(s.pnl, { signDisplay: 'always' })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-xs text-ink-faint italic">Sin trades cerrados aún.</div>
                    )}
                  </Card>
                </Section>

                <Section
                  title="Últimos trades"
                  action={
                    <Link
                      to="/trades"
                      className="text-[11px] uppercase tracking-[0.18em] text-ink-subtle hover:text-accent-500 transition-colors"
                    >
                      Ver todos →
                    </Link>
                  }
                >
                  <Card padded={false} className="p-3">
                    {recentTrades && recentTrades.length > 0 ? (
                      <div className="space-y-1.5">
                        {recentTrades.map((t) => (
                          <RecentTradeRow key={t.id} trade={t} onClick={() => setDetailTrade(t)} />
                        ))}
                      </div>
                    ) : (
                      <div className="text-xs text-ink-faint italic p-3">No hay trades aún.</div>
                    )}
                  </Card>
                </Section>
              </div>
            </div>
          </>
        )}
      </div>

      <TradeDetailModal
        trade={detailTrade}
        accounts={accounts ?? []}
        onClose={() => setDetailTrade(null)}
      />
      {creating && accounts && (
        <TradeFormModal
          open={creating}
          onClose={() => setCreating(false)}
          accounts={accounts}
          defaultAccountId={accountId}
        />
      )}
    </AppLayout>
  );
}

function The5ersCockpit({ d }: { d: The5ersResponse }): ReactElement {
  const clamp = (v: number, max: number): number => Math.max(0, Math.min(100, (v / max) * 100));
  return (
    <Card className="mb-8">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="text-[11px] text-ink-subtle uppercase tracking-[0.2em]">
            Cuenta de fondeo · The5ers
          </div>
          <div className="font-display text-2xl text-ink">
            {d.cuenta}
            <span className="text-ink-subtle text-sm font-sans"> · ${fmtMoney(d.balance)}</span>
          </div>
        </div>
        {d.targetAlcanzado && (
          <span className="text-xs font-bold text-win-DEFAULT bg-win-DEFAULT/10 border border-win-DEFAULT/30 px-2.5 py-1 rounded-full">
            🎯 Target alcanzado
          </span>
        )}
      </div>
      <div className="grid sm:grid-cols-3 gap-5">
        <ProgressMetric
          label={`Objetivo +${d.targetPct}%`}
          value={`${d.profitPct >= 0 ? '+' : ''}${d.profitPct}%`}
          sub={d.targetAlcanzado ? '¡fase superada!' : `faltan ${d.faltaTargetPct}%`}
          pct={clamp(Math.max(0, d.profitPct), d.targetPct)}
          tone="win"
        />
        <ProgressMetric
          label={`DD total · máx ${d.maxTotalPct}%`}
          value={`${d.ddTotalPct}%`}
          sub={`margen ${(d.maxTotalPct - d.ddTotalPct).toFixed(1)}%`}
          pct={clamp(d.ddTotalPct, d.maxTotalPct)}
          tone="loss"
        />
        <div>
          <div className="text-[11px] text-ink-subtle uppercase tracking-wider mb-1">
            Días rentables
          </div>
          <div className="font-mono text-2xl font-bold text-ink tabular-nums">
            {d.diasRentables}
            <span className="text-ink-subtle text-base"> / {d.minDays}</span>
          </div>
          <div className="text-[10px] text-ink-subtle mt-2">
            P&L hoy{' '}
            <span
              className={
                d.pnlHoy > 0 ? 'text-win-DEFAULT' : d.pnlHoy < 0 ? 'text-loss-DEFAULT' : ''
              }
            >
              {fmtMoney(d.pnlHoy, { signDisplay: 'always' })}
            </span>
          </div>
        </div>
      </div>
      <div className="text-[10px] text-ink-faint mt-4">
        Sobre P&L cerrado (mismo criterio que The5ers). Njord vigila el equity en vivo y te avisa
        si el riesgo del día se acerca al límite.
      </div>
    </Card>
  );
}

function PayoutWidget({ s }: { s: PayoutSummary }): ReactElement {
  const fmtMoneyCur = (n: number): string =>
    new Intl.NumberFormat('es-CL', { style: 'currency', currency: s.currency, maximumFractionDigits: 0 }).format(n);
  const fmtShortDate = (iso: string | null): string =>
    iso ? new Intl.DateTimeFormat('es-CL', { day: '2-digit', month: 'short' }).format(new Date(iso)) : '—';
  const proxDias =
    s.proximaVentanaEstimada != null
      ? Math.ceil((new Date(s.proximaVentanaEstimada).getTime() - Date.now()) / (24 * 60 * 60 * 1000))
      : null;
  const ventanaLista = proxDias != null && proxDias <= 0;

  return (
    <Card className="mb-8">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="text-[11px] text-ink-subtle uppercase tracking-[0.2em]">
            Ingreso del trading · payouts
          </div>
          <div className="font-display text-2xl text-ink">
            {fmtMoneyCur(s.totalCobrado)}
            <span className="text-ink-subtle text-sm font-sans"> cobrado</span>
          </div>
        </div>
        <Link
          to="/payouts"
          className="text-[11px] uppercase tracking-[0.18em] text-ink-subtle hover:text-accent-500 transition-colors"
        >
          Ver payouts →
        </Link>
      </div>
      <div className="grid sm:grid-cols-3 gap-5">
        <div>
          <div className="text-[11px] text-ink-subtle uppercase tracking-wider mb-1">Pendiente</div>
          <div className="font-mono text-2xl font-bold text-ink tabular-nums">
            {fmtMoneyCur(s.totalPendiente)}
          </div>
          <div className="text-[10px] text-ink-subtle mt-2">
            {s.cantidadPendientes} pedido{s.cantidadPendientes === 1 ? '' : 's'} sin cobrar
          </div>
        </div>
        <div>
          <div className="text-[11px] text-ink-subtle uppercase tracking-wider mb-1">Promedio / pago</div>
          <div className="font-mono text-2xl font-bold text-ink tabular-nums">
            {fmtMoneyCur(s.promedioPago)}
          </div>
          <div className="text-[10px] text-ink-subtle mt-2">
            {s.cantidadPagos} pago{s.cantidadPagos === 1 ? '' : 's'}
            {s.ultimoPago ? ` · último ${fmtShortDate(s.ultimoPago)}` : ''}
          </div>
        </div>
        <div>
          <div className="text-[11px] text-ink-subtle uppercase tracking-wider mb-1">Próxima ventana</div>
          <div
            className={`font-mono text-2xl font-bold tabular-nums ${ventanaLista ? 'text-win-DEFAULT' : 'text-ink'}`}
          >
            {s.proximaVentanaEstimada
              ? ventanaLista
                ? 'disponible'
                : `en ${proxDias}d`
              : '—'}
          </div>
          <div className="text-[10px] text-ink-subtle mt-2">
            {s.proximaVentanaEstimada ? fmtShortDate(s.proximaVentanaEstimada) : `ciclo ${s.cicloDias}d`}
          </div>
        </div>
      </div>
      <div className="text-[10px] text-ink-faint mt-4">
        El ingreso real es lo cobrado. Registrá cada retiro en Payouts para llevar el ciclo completo.
      </div>
    </Card>
  );
}

function ProgressMetric({
  label,
  value,
  sub,
  pct,
  tone,
}: {
  label: string;
  value: string;
  sub: string;
  pct: number;
  tone: 'win' | 'loss';
}): ReactElement {
  return (
    <div>
      <div className="text-[11px] text-ink-subtle uppercase tracking-wider mb-1">{label}</div>
      <div className="font-mono text-2xl font-bold text-ink tabular-nums">{value}</div>
      <div className="h-1.5 rounded-full bg-surface-border mt-2 overflow-hidden">
        <div
          className={`h-full rounded-full ${tone === 'win' ? 'bg-win-DEFAULT' : 'bg-loss-DEFAULT'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="text-[10px] text-ink-subtle mt-1">{sub}</div>
    </div>
  );
}

function CalendarLegend(): ReactElement {
  return (
    <div className="flex items-center justify-center gap-5 mt-3 text-[10px] text-ink-subtle uppercase tracking-wider">
      <span className="flex items-center gap-1.5">
        <span className="inline-block w-2.5 h-2.5 rounded-sm bg-win-DEFAULT/60" /> ganancia
      </span>
      <span className="flex items-center gap-1.5">
        <span className="inline-block w-2.5 h-2.5 rounded-sm bg-loss-DEFAULT/60" /> pérdida
      </span>
      <span className="flex items-center gap-1.5">
        <span className="inline-block w-2.5 h-2.5 rounded-full bg-info-DEFAULT" /> abierto
      </span>
      <span className="flex items-center gap-1.5">
        <span className="text-warn-DEFAULT font-bold">⚠</span> pendiente
      </span>
    </div>
  );
}

function RecentTradeRow({ trade, onClick }: { trade: Trade; onClick: () => void }): ReactElement {
  const isLong = trade.side === 'long';
  const isOpen = trade.status === 'open';
  const pnl = trade.pnl ?? 0;
  return (
    <button
      onClick={onClick}
      className="w-full text-left rounded-lg px-2.5 py-2 hover:bg-surface-panel/70 transition-colors flex items-center gap-3"
    >
      <span
        className={`shrink-0 w-7 h-7 rounded-md flex items-center justify-center font-bold text-sm ${
          isLong
            ? 'bg-win-DEFAULT/12 text-win-DEFAULT'
            : 'bg-loss-DEFAULT/12 text-loss-DEFAULT'
        }`}
      >
        {isLong ? '↑' : '↓'}
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="font-mono text-xs font-bold text-ink truncate">{trade.symbol}</span>
          {isOpen && (
            <span className="text-[8px] font-bold uppercase tracking-wider text-info-DEFAULT bg-info-DEFAULT/10 border border-info-DEFAULT/30 px-1.5 rounded">
              live
            </span>
          )}
        </div>
        <div className="text-[10px] text-ink-subtle">
          {new Date(trade.entryAt).toLocaleDateString('es-CL', {
            day: '2-digit',
            month: 'short',
          })}
        </div>
      </div>
      <span
        className={`font-mono text-xs font-bold tabular-nums ${
          isOpen
            ? 'text-info-DEFAULT'
            : pnl > 0
            ? 'text-win-DEFAULT'
            : pnl < 0
            ? 'text-loss-DEFAULT'
            : 'text-ink-muted'
        }`}
      >
        {isOpen ? '●' : trade.pnl != null ? fmtMoney(trade.pnl, { signDisplay: 'always' }) : '—'}
      </span>
    </button>
  );
}

function OnboardingHero(): ReactElement {
  return (
    <Card
      tone="accent"
      padded={false}
      className="p-8 sm:p-12 text-center bg-grain bg-grain"
    >
      <div className="max-w-md mx-auto">
        <div className="w-14 h-14 mx-auto mb-5 rounded-2xl bg-gold-sheen flex items-center justify-center text-stone-950 text-2xl font-bold">
          ✦
        </div>
        <h2 className="font-display text-3xl text-ink mb-2">Empezá tu journal.</h2>
        <p className="text-sm text-ink-muted mb-6">
          Configurá tu primera cuenta — fondeo, personal o demo — y empezá a registrar trades con
          contexto, screenshots y análisis.
        </p>
        <Link to="/accounts">
          <Button variant="primary" size="lg">
            + Crear cuenta
          </Button>
        </Link>
      </div>
    </Card>
  );
}
